import type { CSSProperties, ReactNode } from "react";

export function SectionBadge({ children }: { children: ReactNode }) {
  if (!children) return null;
  return <span className="site-badge block">{children}</span>;
}

export function DualHeading({
  as: Tag = "h2",
  align = "center",
  title,
  className = "",
}: {
  as?: "h1" | "h2";
  align?: "center" | "left";
  title: string;
  className?: string;
}) {
  return (
    <Tag
      className={`${Tag === "h1" ? "site-h1" : "site-h2"} text-[var(--public-paper)] ${
        align === "center" ? "text-center" : "text-left"
      } ${className}`}
    >
      {title}
    </Tag>
  );
}

/** Badge + title on the left, lead paragraph on the right (stacked below 1200px). */
export function SplitHeader({
  badge,
  title,
  description,
  titleClassName = "",
  align = "center",
}: {
  badge?: string;
  title: string;
  description?: string;
  titleClassName?: string;
  align?: "center" | "end";
}) {
  return (
    <div
      className={`flex flex-col gap-6 min-[810px]:gap-8 min-[1200px]:flex-row min-[1200px]:justify-between ${
        align === "end" ? "min-[1200px]:items-end" : "min-[1200px]:items-center"
      }`}
    >
      <div className={`flex flex-col gap-2 min-[1200px]:gap-[11px] ${titleClassName}`}>
        <SectionBadge>{badge}</SectionBadge>
        {title ? <DualHeading align="left" title={title} /> : null}
      </div>
      {description ? <p className="site-lead min-[1200px]:w-[382px] min-[1200px]:shrink-0">{description}</p> : null}
    </div>
  );
}

export function PillButton({
  href,
  children,
  variant = "solid",
  size = "md",
  className = "",
  arrow = false,
  onClick,
}: {
  href: string;
  children: ReactNode;
  variant?: "solid" | "ghost";
  size?: "md" | "sm";
  className?: string;
  /** Adds the 45° arrow and the shared CTA hover: the arrow moves up and right, a line draws under the label. */
  arrow?: boolean;
  onClick?: () => void;
}) {
  const cls =
    variant === "solid"
      ? "site-btn"
      : "inline-flex items-center justify-center rounded-2xl border border-[var(--public-accent)] px-6 py-3 text-[18px] font-semibold text-[var(--public-accent)]";
  return (
    <a
      href={href}
      onClick={(event) => {
        if (href.startsWith("#")) event.preventDefault();
        onClick?.();
      }}
      className={`${cls} ${size === "sm" ? "site-btn-sm" : ""} ${arrow ? "site-cta-45" : ""} ${className}`}
    >
      {arrow ? (
        <>
          <span className="site-cta-label">{children}</span>
          <ArrowUpRight className="site-cta-arrow h-[12px] w-[12px]" />
        </>
      ) : (
        children
      )}
    </a>
  );
}

/**
 * Infinite horizontal ticker. Children are rendered twice so the -50% loop is seamless;
 * each item carries its own trailing gap for the same reason.
 */
export function Marquee({
  children,
  duration = 40,
  reverse = false,
  className = "",
  fade = true,
  align = "center",
}: {
  children: ReactNode;
  duration?: number;
  reverse?: boolean;
  className?: string;
  fade?: boolean;
  align?: "center" | "start" | "end";
}) {
  const items = align === "end" ? "items-end" : align === "start" ? "items-start" : "items-center";
  const mask = fade
    ? "linear-gradient(90deg, transparent 0%, #000 12.5%, #000 87.5%, transparent 100%)"
    : undefined;
  return (
    <div
      className={`site-marquee-host overflow-hidden ${className}`}
      style={{ maskImage: mask, WebkitMaskImage: mask }}
    >
      <div
        className={`site-marquee ${reverse ? "site-marquee-reverse" : ""}`}
        style={{ "--marquee-duration": `${duration}s` } as CSSProperties}
      >
        <div className={`flex shrink-0 ${items}`}>{children}</div>
        <div className={`flex shrink-0 ${items}`} aria-hidden="true">
          {children}
        </div>
      </div>
    </div>
  );
}

export function StarIcon({ className = "h-[15px] w-[15px]", color = "#FBBC04" }: { className?: string; color?: string }) {
  return (
    <svg viewBox="0 0 15 15" className={className} aria-hidden="true">
      <path
        d="M7.48473 10.6034L3.81856 12.8427L4.81534 8.66397L1.55273 5.86919L5.83496 5.52588L7.48473 1.55933L9.13456 5.52588L13.4168 5.86919L10.1542 8.66397L11.1509 12.8427L7.48473 10.6034Z"
        fill={color}
      />
    </svg>
  );
}

export function ArrowUpRight({ className = "h-[11px] w-[11px]", color = "currentColor" }: { className?: string; color?: string }) {
  return (
    <svg viewBox="0 0 10.833 10.833" className={className} aria-hidden="true">
      <path d="M 1.167 10.833 L 0 9.667 L 8 1.667 L 0.833 1.667 L 0.833 0 L 10.833 0 L 10.833 10 L 9.167 10 L 9.167 2.833 Z" fill={color} />
    </svg>
  );
}

export function publicCardStyle(): CSSProperties {
  return {
    background: "#151E1E",
    boxShadow: "0 4px 9px rgba(0, 0, 0, 0.07)",
  };
}
